/**
 * Gamepad input, polled once per tick rather than event-driven — the Gamepad
 * API has no `change` event, so `docs/DESIGN.md`'s "Gamepad API with the same
 * DAS/ARR semantics" means diffing button state against the previous poll and
 * feeding the same `InputController` every other device uses.
 *
 * `diffGamepad` is the pure half: given two button-state snapshots, it calls
 * `press`/`release` for whatever changed and returns the actions a new press
 * fired. `pollGamepad` is the thin browser-facing wrapper around
 * `navigator.getGamepads()`.
 */
import type { ControllerAction, InputController } from './input-controller.js';

export interface GamepadSnapshot {
  readonly buttons: readonly boolean[];
}

/** Standard gamepad mapping: D-pad for movement, face buttons for the rest. */
export const DEFAULT_GAMEPAD_BINDINGS: Readonly<Record<number, ControllerAction>> = {
  14: 'left', // D-pad left
  15: 'right', // D-pad right
  13: 'softDrop', // D-pad down
  12: 'hardDrop', // D-pad up
  0: 'rotateCW', // A / Cross
  1: 'rotateCCW', // B / Circle
  2: 'hold', // X / Square
  3: 'hardDrop', // Y / Triangle
};

function buttonPressed(snapshot: GamepadSnapshot | null, index: number): boolean {
  return snapshot?.buttons[index] ?? false;
}

/** Diff two snapshots and drive `controller` from the difference. Returns the actions a new press fired. */
export function diffGamepad(
  previous: GamepadSnapshot | null,
  current: GamepadSnapshot,
  controller: InputController,
  bindings: Readonly<Record<number, ControllerAction>> = DEFAULT_GAMEPAD_BINDINGS,
): readonly ControllerAction[] {
  const fired: ControllerAction[] = [];

  for (const [indexKey, action] of Object.entries(bindings)) {
    const index = Number(indexKey);
    const isDown = buttonPressed(current, index);
    const wasDown = buttonPressed(previous, index);
    if (isDown && !wasDown) fired.push(...controller.press(action));
    else if (!isDown && wasDown) controller.release(action);
  }

  return fired;
}

export interface PollGamepadResult {
  readonly snapshot: GamepadSnapshot | null;
  readonly fired: readonly ControllerAction[];
}

/** Read pad 0 from `getGamepads`, diff it against `previous`, and drive `controller`. */
export function pollGamepad(
  controller: InputController,
  previous: GamepadSnapshot | null,
  bindings: Readonly<Record<number, ControllerAction>> = DEFAULT_GAMEPAD_BINDINGS,
  getGamepads: () => readonly (Gamepad | null)[] = () => navigator.getGamepads(),
): PollGamepadResult {
  const pads = getGamepads();
  const pad = pads[0];
  if (pad === undefined || pad === null) {
    if (previous !== null) controller.releaseAll();
    return { snapshot: null, fired: [] };
  }

  const snapshot: GamepadSnapshot = { buttons: pad.buttons.map((b) => b.pressed) };
  const fired = diffGamepad(previous, snapshot, controller, bindings);
  return { snapshot, fired };
}
